import React from 'react';
import { Dispatch } from '@reduxjs/toolkit';

import { BaseModal, Button, CallbackErrorAxios, CallbackWarningAxios, Client, DownloadIcon, MultiSelect } from '@umanforge/common';

import { useTranslation } from 'react-i18next';
import { useExport, ExportFilters } from '../hooks/export.hook';
import { ClientsIcon } from 'components/icons/clients_icon.component';


interface ExportModalProps {
  title: string;
  isOpen: boolean;
  exportFunction: ( client: Client, token: string | null, dispatch: Dispatch, onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios, filters?: ExportFilters ) => Promise<boolean>;
  onClose: () => void;
  showSiteFilter?: boolean;
  showSectorFilter?: boolean;
}

export const ExportModal: React.FC<ExportModalProps> = ( { title, isOpen, exportFunction, onClose, showSiteFilter = false, showSectorFilter = false } ) => {

  const { t } = useTranslation();
  const {
    allowedClients,
    selectedClients,
    setSelectedClients,
    allowedSites,
    selectedSites,
    setSelectedSites,
    allowedSectors,
    selectedSectors,
    setSelectedSectors,
    tryExportData,
    canExport,
    displayModal,
  } = useExport( isOpen, exportFunction, onClose, showSiteFilter, showSectorFilter );

  const handleClose = () => {
    onClose();
  };

  const getSelectedFilters = (): ExportFilters => {
    const filters: ExportFilters = {};

    if ( selectedSites.length > 0 ) {
      filters.filterSites = selectedSites.map( item => { return item.value; } );
    }

    if ( selectedSectors.length > 0 ) {
      filters.filterSectors = selectedSectors.map( item => { return item.value; } );
    }

    return filters;
  };

  return (
    <BaseModal
      title={ title }
      isOpen={ isOpen && displayModal }
      onClose={ handleClose }
      iconColor="#ef4444"
    >
      <div className='flex flex-col m-8 gap-4'>
        <MultiSelect
          required
          selectedValues={ selectedClients }
          label={ t( 'Client' ) }
          placeholder={ t( 'No associated clients' ) }
          icon={ <ClientsIcon className='w-7 h-7' /> }
          getValues={ allowedClients }
          onChange={ option => { setSelectedClients( option ); } }
        />

        {
          showSiteFilter && allowedSites.length > 0 ?
            <MultiSelect
              label={ t( 'Site' ) }
              placeholder={ t( 'No associated sites' ) }
              getValues={ allowedSites }
              selectedValues={ selectedSites }
              clearable={ false }
              onChange={ option => { setSelectedSites( option ); } }
            />
            :
            null
        }

        {
          showSectorFilter && allowedSectors.length > 0 ?
            <MultiSelect
              label={ t( 'Sector' ) }
              placeholder={ t( 'No associated sectors' ) }
              getValues={ allowedSectors }
              selectedValues={ selectedSectors }
              clearable={ false }
              onChange={ option => { setSelectedSectors( option ); } }
            />
            :
            null
        }

        <div className='flex'>
          <Button
            disable={ !canExport }
            label={ t( 'common:export' ) }
            className={ `w-full m-1 ${ canExport ? '' : 'bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C]' } text-sm font-medium text-white`}
            icon={ <DownloadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
            onClick={ () => {
              if ( selectedClients.length > 0 ) {
                const filters = getSelectedFilters();
                for ( const selectedClient of selectedClients ) {
                  tryExportData( selectedClient.object, filters )
                    .then( () => {
                      onClose();
                    } );
                }
              }
            } }
          />
        </div>
      </div>
    </BaseModal>
  );

};